import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import type { DestinationId, DestinationRunStatus } from '../../lib/types';
import { DESTINATIONS } from '../../lib/destinations';
import { StatusBadge } from '../ui/StatusBadge';
import { cn, formatNumber } from '../../lib/utils';
interface Props {
  selected: DestinationId[];
  totalRecords: number;
  onComplete: (results: Record<DestinationId, DestinationRunStatus>) => void;
}
// Deterministic end-states for the demo: Five9 fails, Teli AI completes.
const FINAL: Record<DestinationId, DestinationRunStatus> = {
  teli: 'completed',
  sendgrid: 'completed',
  five9: 'failed'
};
export function ProcessingView({
  selected,
  totalRecords,
  onComplete
}: Props) {
  const [progress, setProgress] = useState(0);
  const [statuses, setStatuses] = useState<Record<string, DestinationRunStatus>>(() => Object.fromEntries(selected.map(id => [id, 'waiting'])));
  useEffect(() => {
    let pct = 0;
    const order = selected;
    let idx = 0;
    // kick off first destination
    setStatuses(s => ({
      ...s,
      [order[0]]: 'processing'
    }));
    const timer = setInterval(() => {
      pct = Math.min(100, pct + 2.5);
      setProgress(pct);
      const boundary = (idx + 1) / order.length * 100;
      if (pct >= boundary && idx < order.length) {
        const finished = order[idx];
        setStatuses(s => ({
          ...s,
          [finished]: FINAL[finished]
        }));
        idx += 1;
        if (idx < order.length) {
          const next = order[idx];
          setStatuses(s => ({
            ...s,
            [next]: 'processing'
          }));
        }
      }
      if (pct >= 100) {
        clearInterval(timer);
        const results = Object.fromEntries(order.map(id => [id, FINAL[id]])) as Record<DestinationId, DestinationRunStatus>;
        setStatuses(results);
        setTimeout(() => onComplete(results), 700);
      }
    }, 130);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const processed = Math.round(progress / 100 * totalRecords);
  const remaining = totalRecords - processed;
  const current = selected.find(id => statuses[id] === 'processing');
  const currentDef = DESTINATIONS.find(d => d.id === current);
  return <div className="mx-auto max-w-3xl space-y-6" data-id="element-375" data-mp="208zavgoi6dur">
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card" data-id="element-376" data-mp="209r6usm0idlk">
        <div className="mb-2 flex items-end justify-between" data-id="element-377" data-mp="2nzlk3ba8u46c">
          <div data-id="element-378" data-mp="2svk3slkmmy0f">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-379" data-mp="2sw3no7tu7wh3">
              Uploading your leads…
            </h3>
            <p className="text-sm text-gray-500" data-id="element-380" data-mp="2f348y81vq6al">
              {currentDef ? `Currently processing: ${currentDef.name}` : 'Finalizing…'}
            </p>
          </div>
          <span className="text-2xl font-bold text-salesforce-600" data-id="element-381" data-mp="36ua224j6n3qd">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-gray-100" data-id="element-382" data-mp="107m5c8p2ios5">
          <motion.div className="h-full rounded-full bg-salesforce-500" animate={{
          width: `${progress}%`
        }} transition={{
          ease: 'linear'
        }} data-id="element-383" data-mp="v913k4u63x1l" />
        </div>
        <div className="mt-3 flex justify-between text-xs text-gray-500" data-id="element-384" data-mp="14zclivaos6rh">
          <span data-id="element-385" data-mp="h8gnvhjjuhcb">{formatNumber(processed)} processed</span>
          <span data-id="element-386" data-mp="hb30hvlarn1e">{formatNumber(remaining)} remaining</span>
        </div>
      </div>

      <div className="space-y-3" data-id="element-387" data-mp="cc9pc9thg84e">
        {selected.map(id => {
        const def = DESTINATIONS.find(d => d.id === id)!;
        const st = statuses[id];
        return <div key={id} className={cn('flex items-center gap-4 rounded-2xl border bg-white p-4 shadow-card transition-colors', st === 'processing' ? 'border-salesforce-300 ring-1 ring-salesforce-200' : 'border-gray-200')} data-id="element-388" data-mp="m6rxbr5xmpu0">
              <img src={def.icon} alt="" className="h-10 w-10 rounded-lg" data-id="element-389" data-mp="c4z9gm3b78ja" />
              <div className="min-w-0 flex-1" data-id="element-390" data-mp="c7byfj7hij5u">
                <p className="text-sm font-semibold text-gray-900" data-id="element-391" data-mp="g619h551vomb">
                  {def.name}
                </p>
                <p className="text-xs text-gray-500" data-id="element-392" data-mp="l7qmurfqwedk">
                  {id === 'teli' && 'Queuing leads into the Teli AI campaign'}
                  {id === 'sendgrid' && 'Enrolling leads in the SendGrid campaign'}
                  {id === 'five9' && 'Adding records to Five9'}
                </p>
              </div>
              <StatusBadge status={st} size="sm" data-id="element-393" data-mp="zx6avznw65pk" />
            </div>;
      })}
      </div>

      <p className="text-center text-xs text-gray-400" data-id="element-394" data-mp="13yrip6lkjtsw">
        You can keep this tab open — large uploads process in the background and
        each destination completes independently.
      </p>
    </div>;
}